/** Cmd+K глобальный поиск.
 *
 * Открывается по Cmd+K (или Ctrl+K), показывает quick-actions + результаты
 * поиска по products (debounce 200ms). Click → router push.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";
import {
  Box,
  FolderTree,
  Gift,
  LayoutDashboard,
  Package,
  ScrollText,
  Search,
  ShoppingBag,
  Star,
  Tag,
  Users,
  UserCog,
  ImageIcon,
  Settings,
} from "lucide-react";

import { api } from "@/lib/api";

const PAGES = [
  { label: "Дашборд",   path: "/",            icon: LayoutDashboard },
  { label: "Товары",    path: "/products",    icon: Box },
  { label: "Категории", path: "/categories",  icon: FolderTree },
  { label: "Заказы",    path: "/orders",      icon: ShoppingBag },
  { label: "Клиенты",   path: "/clients",     icon: Users },
  { label: "Отзывы",    path: "/reviews",     icon: Star },
  { label: "Промокоды", path: "/promo-codes", icon: Tag },
  { label: "Бонусы",    path: "/bonuses",     icon: Gift },
  { label: "Склад",     path: "/stock-log",   icon: ScrollText },
  { label: "Баннеры",   path: "/banners",     icon: ImageIcon },
  { label: "Настройки", path: "/settings",    icon: Settings },
  { label: "Юзеры",     path: "/users",       icon: UserCog },
];

interface ProductHit {
  id: number;
  name: string;
  sku: string;
}

export function CommandPalette(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Cmd+K / Ctrl+K toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounce query
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(id);
  }, [query]);

  // Product search
  const { data: productsHit } = useQuery({
    queryKey: ["cmdk-products", debouncedQuery],
    queryFn: async () => {
      if (debouncedQuery.length < 2) return [] as ProductHit[];
      const { data } = await api.get("/products", {
        params: { q: debouncedQuery, per_page: 10 },
      });
      return (data?.data ?? []) as ProductHit[];
    },
    staleTime: 30_000,
    enabled: debouncedQuery.length >= 2,
  });

  const go = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  // focus input when open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery("");
    }
  }, [open]);

  const filteredPages = useMemo(() => {
    if (!debouncedQuery) return PAGES;
    const q = debouncedQuery.toLowerCase();
    return PAGES.filter((p) => p.label.toLowerCase().includes(q));
  }, [debouncedQuery]);

  if (!open) return <></>;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 backdrop-blur-sm pt-[15vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl rounded-xl border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <Command label="Глобальный поиск" shouldFilter={false}>
          <div className="flex items-center gap-3 border-b px-4 py-3">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Найти товар, страницу, действие…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <kbd className="hidden sm:inline rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground">
              ESC
            </kbd>
          </div>

          <Command.List className="max-h-[60vh] overflow-y-auto p-2">
            {filteredPages.length === 0 &&
              (productsHit?.length ?? 0) === 0 && (
                <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
                  Ничего не найдено
                </Command.Empty>
              )}

            {filteredPages.length > 0 && (
              <Command.Group
                heading="Страницы"
                className="text-xs text-muted-foreground px-2 pt-1 pb-1"
              >
                {filteredPages.map((p) => {
                  const Icon = p.icon;
                  return (
                    <Command.Item
                      key={p.path}
                      onSelect={() => go(p.path)}
                      className="flex items-center gap-3 px-3 py-2 text-sm rounded-md cursor-pointer aria-selected:bg-primary-foreground/10 hover:bg-muted"
                    >
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-foreground">{p.label}</span>
                    </Command.Item>
                  );
                })}
              </Command.Group>
            )}

            {productsHit && productsHit.length > 0 && (
              <Command.Group
                heading="Товары"
                className="text-xs text-muted-foreground px-2 pt-3 pb-1"
              >
                {productsHit.map((p) => (
                  <Command.Item
                    key={p.id}
                    onSelect={() => go(`/products/${p.id}/edit`)}
                    className="flex items-center gap-3 px-3 py-2 text-sm rounded-md cursor-pointer aria-selected:bg-primary-foreground/10 hover:bg-muted"
                  >
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-foreground">{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.sku}
                      </div>
                    </div>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>

          <div className="border-t px-3 py-2 text-[10px] text-muted-foreground flex items-center justify-between">
            <span>↑↓ навигация · ↵ открыть · esc закрыть</span>
            <span>
              <kbd className="rounded border px-1">⌘</kbd>+
              <kbd className="rounded border px-1">K</kbd>
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}
